import { NavLink, useLocation } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import {
  Badge, Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar,
} from '@/lib/macondo';
import { useAuth } from '@/auth/AuthContext';
import { hasRole } from '@/auth/roles';
import { usePortalConfigured } from '@/api/portal';
import { useApprovals } from '@/api/controls';
import { activeNavItem, NAV, type NavItem } from './nav';

type BadgeKind = NonNullable<NavItem['badge']>;

/** A badge is a bare number on screen; readers need telling what it counts. */
const BADGE_LABEL: Record<BadgeKind, (n: number) => string> = {
  approvals: (n) => `${n} invoice${n === 1 ? '' : 's'} waiting for approval`,
};

export function AppSidebar() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isMobile, setOpenMobile } = useSidebar();
  const active = activeNavItem(pathname);
  const portalConfigured = usePortalConfigured();
  // The open-items query the Approvals page already uses, so the two share a cache entry.
  const approvals = useApprovals(false);
  const pendingApprovals = approvals.data?.items.filter((i) => i.status === 'pending').length ?? 0;
  const badgeCount = (badge: BadgeKind): number => (badge === 'approvals' ? pendingApprovals : 0);

  return (
    <Sidebar aria-label="Main navigation">
      <SidebarHeader>
        <div className="flex items-center gap-200 py-100">
          <Receipt size={20} aria-hidden="true" className="text-typography-primary" />
          <span className="text-callout-semibold text-typography-primary whitespace-nowrap">Spoonity Billing</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {NAV.map((section) => {
          const items = section.items.filter((i) => (!i.role || hasRole(user?.role, i.role)) && (i.feature !== 'portal' || portalConfigured));
          if (items.length === 0) return null;
          return (
            <SidebarGroup key={section.id}>
              <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => {
                    const count = item.badge ? badgeCount(item.badge) : 0;
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton asChild isActive={active?.path === item.path}>
                          <NavLink
                            to={item.path}
                            end
                            onClick={() => {
                              if (isMobile) setOpenMobile(false);
                            }}
                          >
                            <item.icon size={16} aria-hidden="true" />
                            <span>{item.label}</span>
                            {item.badge && count > 0 && (
                              <Badge size="sm" variant="default1" className="ml-auto" aria-label={BADGE_LABEL[item.badge](count)}>
                                {count}
                              </Badge>
                            )}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
    </Sidebar>
  );
}
